"use client";

import React from "react";
import { CircleCheck, Loader, MapPin, Globe } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICES_OPTIONS = ["SEO", "Local SEO", "Strategy", "Paid Search", "Social Media", "Branding"];
const BENEFITS = ["Revenue-driven marketing approach", "Enhanced SEO visibility", "Lead generation focus", "Customized strategies for small and local businesses"];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-bold text-primary">Assessment Requested!</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  const inputClasses = "flex h-12 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Your Name *</FormLabel>
              <FormControl>
                <input type="text" placeholder="John Doe" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Email Address *</FormLabel>
              <FormControl>
                <input type="email" placeholder="john@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Phone Number *</FormLabel>
              <FormControl>
                <input type="tel" placeholder="(555) 123-4567" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Company / Organization</FormLabel>
              <FormControl>
                <input type="text" placeholder="Acme Corp" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="services" rules={{
      required: "Please select at least one service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Services You're Interested In *</FormLabel>
            <p className="text-sm text-muted-foreground mb-3">Select all that apply</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICES_OPTIONS.map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center">
                    <input type="checkbox" className="peer h-5 w-5 appearance-none rounded-none border border-input bg-background checked:border-primary checked:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all cursor-pointer" checked={Array.isArray(field.value) && field.value.includes(option)} onChange={e => {
              const current = Array.isArray(field.value) ? field.value : [];
              const updated = e.target.checked ? [...current, option] : current.filter(val => val !== option);
              field.onChange(updated);
            }} />
                    <CircleCheck className="absolute h-3.5 w-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
                  </div>
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">{option}</span>
                </label>)}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Preferred Consultation Date</FormLabel>
            <p className="text-sm text-muted-foreground mb-2">Choose a date that works for you (optional)</p>
            <FormControl>
              <input type="date" className={inputClasses} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Additional Details / Goals</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your marketing goals..." className="flex min-h-[120px] w-full rounded-none border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" variant="secondary" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-none uppercase tracking-wide hover:bg-secondary/90 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Submitting Request...
          </> : 'Schedule My Free Assessment'}
      </Button>
    </Form>;
}
export default function ScheduleAConsultation() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="schedule-a-consultation" className="w-full py-24 bg-background text-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 space-y-8 transition-all duration-700 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-primary font-sans tracking-tight mb-6">
                Ready to Grow Your Business?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Stop guessing with your marketing budget. Schedule a free consultation and get a customized assessment designed to increase your visibility, drive more leads, and grow your revenue.
              </p>
            </div>

            <div className="space-y-6 pt-6 border-t border-border">
              <h3 className="text-xl font-semibold text-primary">What to expect:</h3>
              <ul className="space-y-4">
                {BENEFITS.map((benefit, i) => <li key={i} data-index={i} className="flex items-start space-x-3">
                    <CircleCheck className="h-6 w-6 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            <div className="space-y-4 pt-6 border-t border-border">
              <div className="flex items-center space-x-3 text-muted-foreground">
                <Globe className="h-5 w-5 text-primary" />
                <span>foxrivermarketing.com</span>
              </div>
              <div className="flex items-center space-x-3 text-muted-foreground">
                <MapPin className="h-5 w-5 text-primary" />
                <span>Serving Small & Local Businesses</span>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-200 ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <Card className="rounded-none border-border shadow-xl bg-card">
              <CardContent className="p-6 !bg-[rgba(225,231,239,1)] md:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-primary mb-2">Schedule a Consultation</h3>
                  <p className="text-muted-foreground">Fill out the form below and our team will be in touch shortly.</p>
                </div>
                
                <FormProvider formId="consultation_form_contact" sectionName="schedule-a-consultation" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                services: [],
                preferred_date: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                services: {
                  required: true
                }
              }} submitText="Schedule My Free Assessment" successMessage="Thank you for reaching out! We have received your request and will contact you shortly to confirm your consultation time.">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}